import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Form, InputGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { Checkbox } from '@Components/FormCheck';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Size } from '@src/Components/designConstants';
import { awaitJSON, FlashtalkingLambdaFetch } from '../../../utils/fetch-utils';
import { AdOpsToolProps } from '../adops-constants';

const PLACEMENT_TYPE_OPTIONS = [
  { label: 'Audio VAST', value: 'audio_vast' },
  { label: 'Video VAST', value: 'video_vast' },
  { label: 'Pixel', value: 'pixel' },
  { label: 'Display', value: 'display' }
] as const;
type FlashtalkingPlacementType = (typeof PLACEMENT_TYPE_OPTIONS)[number]['value'];

interface FlashtalkingSiteRow {
  id: number;
  name: string;
  pretty_name: string;
  network_contacts: string[];
  pixel: string;
  placement_types: FlashtalkingPlacementType[];
  streaming_networks_short_codes: string[];
  url: string;
}

// Unique, sorted values across every site — surfaced as dropdown options so it's
// easy to see what's already in use while still allowing new entries (creatable).
const uniqueSorted = (values: string[]): DropdownOption[] =>
  [...new Set(values)]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ value, label: value }));

export const FlashtalkingSiteAdder = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const [flashtalkingSites, setFlashtalkingSites] = useState<FlashtalkingSiteRow[]>();
  const [networkContacts, setNetworkContacts] = useState<string[]>([]);
  const [placementTypes, setPlacementTypes] = useState<FlashtalkingPlacementType[]>([]);
  const [prettyName, setPrettyName] = useState<string>();
  const [selectedFlashtalkingSite, setSelectedFlashtalkingSite] = useState<FlashtalkingSiteRow>();
  const [siteID, setSiteID] = useState<number>();
  const [siteName, setSiteName] = useState<string>();
  const [streamingNetworkShortCodes, setStreamingNetworkShortCodes] = useState<string[]>([]);
  const [url, setURL] = useState<string>();

  const { setError } = utils;
  const siteMap: Record<string, FlashtalkingSiteRow> = Object.fromEntries(
    (flashtalkingSites ?? []).map(site => [`${site.id}`, site])
  );

  useEffect(() => {
    (async () => {
      if (!flashtalkingSites) {
        const sitesResp = await FlashtalkingLambdaFetch('/getFlashtalkingSites', {
          method: 'GET',
          params: {}
        });
        setFlashtalkingSites(await awaitJSON<FlashtalkingSiteRow[]>(sitesResp));
      }
    })();
  }, [flashtalkingSites]);

  const siteOptions: DropdownOption[] = (flashtalkingSites ?? []).map(site => ({
    value: `${site.id}`,
    label: `${site.name} (${site.id})`
  }));

  const contactOptions = useMemo(
    () => uniqueSorted((flashtalkingSites ?? []).flatMap(site => site.network_contacts ?? [])),
    [flashtalkingSites]
  );

  const shortCodeOptions = useMemo(
    () =>
      uniqueSorted(
        (flashtalkingSites ?? []).flatMap(site => site.streaming_networks_short_codes ?? [])
      ),
    [flashtalkingSites]
  );

  const selectSite = (id: string) => {
    const site = siteMap[id];
    if (!site) {
      return;
    }
    setSelectedFlashtalkingSite(site);
    setNetworkContacts(site.network_contacts ?? []);
    setSiteID(site.id);
    setSiteName(site.name);
    setPrettyName(site.pretty_name);
    setStreamingNetworkShortCodes(site.streaming_networks_short_codes ?? []);
    setURL(site.url);
    setPlacementTypes(site.placement_types ?? []);
  };

  const togglePlacementType = (type: FlashtalkingPlacementType) => {
    setPlacementTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...new Set([type, ...prev])].sort((a, b) => a.localeCompare(b))
    );
  };

  const addFlashtalkingSite = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await FlashtalkingLambdaFetch('/addFlashtalkingSite', {
        method: 'POST',
        body: {
          site: {
            id: siteID,
            name: siteName,
            pretty_name: prettyName,
            network_contacts: networkContacts.filter(email => email !== ''),
            placement_types: placementTypes,
            pixel: null,
            streaming_networks_short_codes: streamingNetworkShortCodes.filter(code => code !== ''),
            url
          }
        }
      });
      setFlashtalkingSites(undefined);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [
    networkContacts,
    placementTypes,
    prettyName,
    setError,
    siteID,
    siteName,
    streamingNetworkShortCodes,
    url
  ]);

  return (
    <Card className='ad-ops-card' data-testid='ft-site'>
      <div className='ad-ops-card-title'>
        Add/Update Flashtalking Site
        <InfoTooltip>
          Use the Associated Streaming Network Short Codes to associate a site with streaming
          networks other than ones we can match on based on the site&apos;s name.
        </InfoTooltip>
      </div>
      <Dropdown
        size={Size.LG}
        className='ad-ops-elem'
        label='Flashtalking Site'
        placeholder='Search a site to edit…'

        menuPortalTarget={document.body}
        searchable
        clearable
        value={selectedFlashtalkingSite ? `${selectedFlashtalkingSite.id}` : null}
        options={siteOptions}
        onChange={value => {
          if (value) {
            selectSite(value);
          }
        }}
      />
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Site ID</InputGroup.Text>
        <Form.Control
          value={siteID ?? ''}
          onChange={e => {
            if (e.target.value === '') {
              setSiteID(undefined);
            }
            if (!Number.isNaN(parseInt(e.target.value, 10))) {
              setSiteID(parseInt(e.target.value, 10));
            }
          }}
        />
      </InputGroup>
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Site Name</InputGroup.Text>
        <Form.Control value={siteName ?? ''} onChange={e => setSiteName(e.target.value)} />
      </InputGroup>
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>BPM Site Pretty Name Name</InputGroup.Text>
        <Form.Control value={prettyName ?? ''} onChange={e => setPrettyName(e.target.value)} />
      </InputGroup>
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>URL</InputGroup.Text>
        <Form.Control value={url ?? ''} onChange={e => setURL(e.target.value)} />
      </InputGroup>
      <Dropdown
        size={Size.LG}
        className='ad-ops-elem'
        label='Network Contacts'
        placeholder='Add a contact email…'

        menuPortalTarget={document.body}
        multiSelect
        searchable
        creatable
        showChips
        clearable
        value={networkContacts}
        options={contactOptions}
        onChange={contacts => setNetworkContacts(contacts)}
        reactSelectProps={{ formatCreateLabel: input => `Add "${input}"` }}
      />
      <Dropdown
        size={Size.LG}
        className='ad-ops-elem'
        label='Associated Streaming Network Short Codes'
        placeholder='Add a short code…'

        menuPortalTarget={document.body}
        multiSelect
        searchable
        creatable
        showChips
        clearable
        value={streamingNetworkShortCodes}
        options={shortCodeOptions}
        onChange={codes => setStreamingNetworkShortCodes(codes)}
        reactSelectProps={{ formatCreateLabel: input => `Add "${input}"` }}
      />
      <InputGroup size='sm' className='ad-ops-elem ad-ops-check-inputs'>
        {PLACEMENT_TYPE_OPTIONS.map(({ label, value }) => (
          <Fragment key={value}>
            <InputGroup.Text className='input-label'>{label}</InputGroup.Text>
            <Checkbox
              checked={placementTypes.includes(value)}
              onChange={() => togglePlacementType(value)}
            />
          </Fragment>
        ))}
      </InputGroup>
      <Button
        size={Size.LG}
        className='ad-ops-elem'
        loading={activeAPICall}
        onClick={addFlashtalkingSite}
        data-testid='ft-site-add'
      >
        Add
      </Button>
    </Card>
  );
};
